import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { getEmployeesWithActiveContract } from '../services/api_calendar';

const getEmail = (emp) => emp?.email_auth || emp?.email || null;

const ListBoxQueryMultiSelectEmployeesActiveContract = ({
  token,
  selectedEmployees = [],
  onChange,
  employeeRolePairs = [],
  placeholder = 'Seleziona dipendenti...',
  pageSize = 10,
  filterBy,
  permission = null,
  ou = null,
  wrapperClassName = '',
  openDirection = 'up',
  positionOptions = 'absolute',
  contractStartDate = null,
  contractEndDate = null,
  sortConfig = null,
  buttonClassName = '',
  viewEmail = false,
  multiple = false,
}) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const listBoxRef = useRef(null);

  const loadOptions = async (reset = false) => {
    try {
      const nextPage = reset ? 1 : page;

      const res = await getEmployeesWithActiveContract(
        token,
        search,
        filterBy,
        null,
        nextPage,
        pageSize,
        ou,
        permission,
        contractStartDate,
        contractEndDate,
        sortConfig
      );

      const employeesArray = Array.isArray(res.results) ? res.results : [];

      const newOptions = employeesArray
        .filter(emp => !employeeRolePairs.some(pair => pair.employeeId === emp.id))
        .map(emp => ({
          name: emp.first_name && emp.last_name ? `${emp.last_name} ${emp.first_name}` : (emp.cf || 'Senza CF'),
          value: emp.id,
          original: emp,
        }));

      setOptions(prev => {
        const combined = reset ? newOptions : [...prev, ...newOptions];
        return Array.from(new Map(combined.map(i => [i.value, i])).values());
      });

      setPage(nextPage + 1);
      setHasMore(employeesArray.length === pageSize);
    } catch (e) {
      console.error('Errore fetch:', e);
    }
  };

  useEffect(() => {
    setOptions(prev => prev.filter(opt => !employeeRolePairs.some(pair => pair.employeeId === opt.value)));
  }, [employeeRolePairs]);

  useEffect(() => {
    setPage(1);
    loadOptions(true);
  }, [search]);

  const handleScroll = (e) => {
    if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 10 && hasMore) {
      loadOptions();
    }
  };

  const toggleSelection = (item) => {
    const exists = selectedEmployees.find(e => e.id === item.value);
    const updated = exists
      ? selectedEmployees.filter(e => e.id !== item.value)
      : [...selectedEmployees, item.original];

    onChange && onChange(updated);
  };

  const listBoxStyles = {
    bottom: openDirection === 'down' ? '100%' : 'auto',
    top: openDirection === 'up' ? '100%' : 'auto',
  };

  return (
    <div className={`w-full ${wrapperClassName}`}>
      <Listbox
        value={multiple ? selectedEmployees : (selectedEmployees[0] || null)}
        onChange={(value) => {
          if (multiple) {
            onChange?.(value);
          } else {
            onChange?.(value ? [value] : []);
          }
        }}
        by="id"
        multiple={multiple}
      >
        <div className="relative mt-1">
          <Listbox.Button
            className={`relative h-10 w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left sm:text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ${
              buttonClassName || 'shadow-md border border-gray-200'
            }`}
          >
            <span className="block truncate text-gray-500">
              {selectedEmployees.length > 0
                ? selectedEmployees
                    .map(
                      emp =>
                        `${emp.last_name ?? ''} ${emp.first_name ?? ''}`.trim() ||
                        emp.cf
                    )
                    .join(', ')
                : placeholder}
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              ref={listBoxRef}
              onScroll={handleScroll}
              className={`${positionOptions} mt-1 w-full max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-30`}
              style={listBoxStyles}
            >
              <div className="px-3 py-2 me-3 bg-white z-10 sticky -top-1">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      'ArrowUp',
                      'ArrowDown',
                      'Enter',
                      'Escape',
                      'Tab',
                    ];

                    if (!allowedKeys.includes(e.key)) {
                      e.stopPropagation();
                    }
                  }}
                  placeholder="Cerca..."
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              {options.length > 0 ? (
                options.map((option) => {
                  const selected = selectedEmployees.some(
                    se => se.id === option.value
                  );

                  const email = getEmail(option.original);

                  return (
                    <Listbox.Option
                      key={option.value}
                      value={option.original}
                      onClick={() => toggleSelection(option)}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                          active
                            ? 'bg-indigo-100 text-gray-900'
                            : 'text-gray-900'
                        }`
                      }
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span
                          className={`truncate ${
                            selected
                              ? 'font-medium'
                              : 'font-normal'
                          }`}
                        >
                          {option.name}
                        </span>

                        {viewEmail && (
                          email ? (
                            <span className="ml-2 shrink-0 text-xs text-gray-500 truncate max-w-[55%]">
                              {email}
                            </span>
                          ) : (
                            <span className="ml-2 shrink-0 text-xs font-semibold text-red-600">
                              — nessuna mail —
                            </span>
                          )
                        )}
                      </div>

                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-800">
                          <CheckIcon className="h-5 w-5" />
                        </span>
                      )}
                    </Listbox.Option>
                  );
                })
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">
                  Nessun risultato
                </div>
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export default ListBoxQueryMultiSelectEmployeesActiveContract;
